<template lang="pug">
div(class="container")
    div(
        ref="tooltipContainer"
        class="tooltipContainer"
        @mouseenter="showTooltipDelay"
        @mouseleave="hideTooltip"
        @focus="showTooltipDelay"
        @blur="hideTooltip"
    )
        slot
    div(
        ref="tooltip"
        class="tl-tooltip"
        :class="{ secondary: secondary }"
        :style="tooltipStyles"
    )
        slot(name="tooltip") {{ tip }}
</template>

<script setup lang="ts">
import { ref, computed, type Ref } from 'vue'
import { type Strategy, useFloating, flip, shift, offset } from '@floating-ui/vue'

type TooltipProps = {
    tip?: string
    top?: boolean
    left?: boolean
    right?: boolean
    delay?: number | string
    strategy?: Strategy
    disabled?: boolean
    zIndex?: number | string
    secondary?: boolean
}

const props = defineProps<TooltipProps>()

const tooltipContainer: Ref<HTMLElement | null> = ref(null)
const tooltip: Ref<HTMLElement | null> = ref(null)
const isOpened = ref<boolean>(false)
const delayTimeout = ref<number | null>(null)

const placement = computed(() => {
    if (props.top) {
        return 'top'
    }

    if (props.left) {
        return 'left'
    }

    if (props.right) {
        return 'right'
    }

    return 'bottom'
})

const { floatingStyles, update } = useFloating(tooltipContainer, tooltip, {
    placement: placement.value,
    strategy: props.strategy ? props.strategy : 'absolute',
    middleware: [flip(), shift(), offset(5)],
})

const tooltipStyles = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styles: Record<string, any> = {
        ...floatingStyles.value,
    }

    if (isOpened.value) {
        styles.zIndex = props.zIndex || '1000'
        styles.opacity = '1'
        styles.visibility = 'visible'
        styles.pointerEvents = 'all'
    } else {
        styles.zIndex = '-1'
        styles.opacity = '0'
        styles.visibility = 'hidden'
        styles.pointerEvents = 'none'
    }

    return styles
})

const showTooltip = () => {
    isOpened.value = true
    update()
}

const clearCurrentTimeout = () => {
    if (delayTimeout.value !== null) clearTimeout(delayTimeout.value)
}

function showTooltipDelay() {
    if (props.disabled) {
        return
    }

    if (props.delay) {
        clearCurrentTimeout()
        delayTimeout.value = window.setTimeout(showTooltip, Number(props.delay))
    } else {
        showTooltip()
    }
}

function hideTooltip() {
    clearCurrentTimeout()
    isOpened.value = false
}
</script>

<style lang="sass" scoped>
.container
    display: inline-block

.tl-tooltip
    background: var(--color-black)
    color: var(--color-white)
    padding: var(--padding-2) var(--padding-4)
    border-radius: var(--border-radius)
    transition: opacity .3s ease-in-out
    &.secondary
        background: var(--color-white)
        padding: var(--padding-2)
        border-radius: var(--border-radius)
        border: 1px solid var(--color-black-tint-light)
        box-shadow: 0 0 10px 0 var(--color-black-tint-medium)
        color: var(--color-black)
</style>
